/**
 * A consulta do sistema que trava a análise (E147.F2–F3, ADR-0101 §2.5): o nome da consulta, se o
 * sistema ainda tenta sozinho ou se as tentativas acabaram, e as duas saídas da equipe — tentar de
 * novo (a ação principal: verifica o caso na hora, com uma chamada a mais) ou registrar a limitação
 * com justificativa, que a análise passa a declarar. Nada é pedido ao banco.
 */
import { useId, useState } from 'react';

import { Button, Field, Textarea } from '@/components/ui';
import {
  JUSTIFICATIVA_MAXIMA,
  JUSTIFICATIVA_MINIMA,
  type AcaoDoPedido,
  type ConsultaTravada,
  type PedidoDaEtapaData,
} from '@/lib/conferencia_das_etapas';
import type { ResultadoDoPedidoDaEtapa } from '@/lib/useConferenciaDasEtapas';

import { nomeDaConsulta, preencher, t } from './textos';

const LIMITES = { min: JUSTIFICATIVA_MINIMA, max: JUSTIFICATIVA_MAXIMA };

export interface ConsultaIndisponivelProps {
  consulta: ConsultaTravada;
  pedir: (pedido: PedidoDaEtapaData) => Promise<ResultadoDoPedidoDaEtapa>;
  /** O pedido atendido (ou já sem falha): o painel avisa, porque a consulta resolvida sai da lista. */
  onFeito: (texto: string) => void;
}

export function ConsultaIndisponivel({
  consulta,
  pedir,
  onFeito,
}: ConsultaIndisponivelProps): JSX.Element {
  const [limitando, setLimitando] = useState(false);
  const [justificativa, setJustificativa] = useState('');
  const [enviando, setEnviando] = useState<AcaoDoPedido | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const idDoTitulo = useId();

  async function enviar(acao: AcaoDoPedido): Promise<void> {
    setEnviando(acao);
    setErro(null);
    const resultado = await pedir({
      acao,
      provedor: consulta.provedor,
      justificativa: acao === 'limitacao' ? justificativa.trim() : null,
    });
    setEnviando(null);
    if (resultado.ok || resultado.recusa === 'sem_falha') {
      setLimitando(false);
      onFeito(resultado.ok ? t.pedidoFeito[acao] : t.recusa.sem_falha);
      return;
    }
    setErro(preencher(t.recusa[resultado.recusa], LIMITES));
  }

  const curta = justificativa.trim().length < JUSTIFICATIVA_MINIMA;
  return (
    <li
      className="space-y-sc-2 border border-border p-sc-4"
      aria-labelledby={idDoTitulo}
      data-testid="consulta-indisponivel"
      data-provedor={consulta.provedor}
      data-esgotada={consulta.esgotada}
    >
      <p id={idDoTitulo} className="text-body-md font-semibold text-fg">
        {preencher(t.consultaTitulo, { consulta: nomeDaConsulta(consulta.provedor) })}
      </p>
      <p className="text-body-sm text-fg">
        {consulta.esgotada ? t.consultaEsgotada : t.consultaARetomar}
      </p>
      <div className="flex flex-wrap gap-sc-2">
        <Button
          type="button"
          size="sm"
          disabled={enviando !== null}
          onClick={() => void enviar('tentativa')}
        >
          {enviando === 'tentativa' ? t.verificando : t.tentarConsulta}
        </Button>
        {!limitando ? (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={enviando !== null}
            onClick={() => setLimitando(true)}
          >
            {t.registrarLimitacao}
          </Button>
        ) : null}
      </div>
      {limitando ? (
        <form
          className="space-y-sc-2"
          onSubmit={(evento) => {
            evento.preventDefault();
            void enviar('limitacao');
          }}
        >
          <Field
            label={t.justificativaRotulo}
            description={preencher(t.justificativaAjuda, LIMITES)}
            required
          >
            <Textarea
              value={justificativa}
              onChange={(evento) => setJustificativa(evento.target.value)}
              rows={3}
              maxLength={JUSTIFICATIVA_MAXIMA}
              data-testid="consulta-justificativa"
            />
          </Field>
          <div className="flex gap-sc-2">
            <Button type="submit" size="sm" disabled={enviando !== null || curta}>
              {enviando === 'limitacao' ? t.enviando : t.salvarLimitacao}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setLimitando(false)}>
              {t.cancelar}
            </Button>
          </div>
        </form>
      ) : null}
      {erro !== null ? (
        <p role="alert" className="text-body-sm text-danger-700" data-testid="consulta-retorno">
          {erro}
        </p>
      ) : null}
    </li>
  );
}
